
import React, { useState } from 'react';
import { 
  Table, 
  TableHeader, 
  TableRow, 
  TableHead, 
  TableBody, 
  TableCell 
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Search, 
  Download, 
  ExternalLink, 
  Check, 
  X,
  Pause
} from 'lucide-react';
import { format } from 'date-fns';
import { ResellerOrder } from '@/types/resellerOrder';

interface OrdersTableProps {
  isLoading: boolean;
  orders: ResellerOrder[];
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onOrderClick: (order: ResellerOrder, e: React.MouseEvent) => void;
  onApproveOrder: (orderId: string) => void;
  onRejectOrder: (orderId: string) => void;
  onHoldOrder: (orderId: string) => void;
  processingOrderId: string | null;
  isProcessing: boolean;
}

const OrdersTable = ({ 
  isLoading, 
  orders, 
  searchTerm, 
  onSearchChange, 
  onOrderClick,
  onApproveOrder,
  onRejectOrder,
  onHoldOrder,
  processingOrderId,
  isProcessing
}: OrdersTableProps) => {
  
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="relative flex-grow">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by business, contact, or order #..."
            className="pl-8"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>
        
        <Button variant="outline" onClick={() => console.log('Export')}>
          <Download className="h-4 w-4 mr-2" />
          Export
        </Button>
      </div>
      
      <div className="rounded-md border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Order #</TableHead>
              <TableHead>Business</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Payment Method</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} className="h-24 text-center">
                  <div className="flex justify-center">
                    <div className="animate-spin h-6 w-6 border-4 border-primary border-t-transparent rounded-full"></div>
                  </div>
                </TableCell>
              </TableRow>
            ) : orders.length > 0 ? (
              orders.map((order) => (
                <TableRow 
                  key={order.id} 
                  className="cursor-pointer hover:bg-gray-50" 
                  onClick={(e) => onOrderClick(order, e)}
                >
                  <TableCell className="font-medium flex items-center">
                    {order.orderNumber}
                    <ExternalLink className="h-3 w-3 ml-1 text-gray-400" />
                  </TableCell>
                  <TableCell>{order.businessName}</TableCell>
                  <TableCell>
                    <div>{order.contactName}</div>
                    <div className="text-xs text-gray-500">{order.contactEmail}</div>
                  </TableCell>
                  <TableCell>{formatCurrency(order.paymentAmount)}</TableCell>
                  <TableCell>{format(new Date(order.orderDate), 'MMM d, yyyy')}</TableCell>
                  <TableCell className="capitalize">{order.paymentMethod.replace('_', ' ')}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200">
                      Pending
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      <Button 
                        variant="success" 
                        size="sm" 
                        onClick={(e) => {
                          e.stopPropagation();
                          onApproveOrder(order.id);
                        }}
                        disabled={isProcessing && processingOrderId === order.id}
                      >
                        {isProcessing && processingOrderId === order.id ? (
                          <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                        ) : (
                          <Check className="h-4 w-4" />
                        )}
                      </Button>
                      <Button 
                        variant="warning" 
                        size="sm" 
                        onClick={(e) => {
                          e.stopPropagation();
                          onHoldOrder(order.id);
                        }}
                        disabled={isProcessing && processingOrderId === order.id}
                      >
                        {isProcessing && processingOrderId === order.id ? (
                          <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                        ) : (
                          <Pause className="h-4 w-4" />
                        )}
                      </Button>
                      <Button 
                        variant="destructive" 
                        size="sm" 
                        onClick={(e) => {
                          e.stopPropagation();
                          onRejectOrder(order.id);
                        }}
                        disabled={isProcessing && processingOrderId === order.id}
                      >
                        {isProcessing && processingOrderId === order.id ? (
                          <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                        ) : (
                          <X className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} className="h-24 text-center">
                  {searchTerm ? 
                    'No orders matching your search criteria' : 
                    'No pending orders at this time'
                  }
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default OrdersTable;
